import type { Events } from '@carvana/core-events';

export const ACCOUNT_MODAL_MODULE = 'CoreAccountModal';
export const APPRAISAL_EMBEDDED_MODULE = 'AppraisalMFE';
export const APPRAISAL_REFRESH_MODULE = 'AppraisalRefreshModule';
export const CHAT_UI_MODULE = 'ChatUi';
export const COMPOSER_MODULE = 'CarvanaComposerUi';
export const SOFTPULL_MODULE = 'SoftPullModule';

export const DEFAULT_MFE_READY_FAILSAFE_MS = 10000;

type MfeEventName = keyof Events;

export type MfeDefinition = {
  // Prefix for Datadog RUM lifecycle actions (`<prefix>-registry-<data>`, `<prefix>-app-ready`).
  datadogActionPrefix?: string;
  moduleName: string;
  // The MFE's application-level "safe to call" event, as opposed to the framework's 'loaded'.
  readyEvent: MfeEventName;
  // Only honor ready/unready payloads whose `source` matches, for events shared across emitters.
  readySource?: string;
  // Replay a ready event that fired before the listener subscribed. Leave off for MFEs that
  // remount per page, where a replay could be a stale ready from a torn-down instance.
  replayReady: boolean;
  unreadyEvent?: MfeEventName;
};

// An MFE that is a self-contained modal: the host asks it to open with `openEvent`, and
// useOpenMfeModal queues that request behind readiness and covers the wait with a placeholder.
export type MfeModalDefinition = MfeDefinition & {
  openEvent: MfeEventName;
};

export const DEFAULT_MFE_MODAL_PLACEHOLDER_MESSAGE =
  'Getting that ready for you...';

// Event names are literals rather than `eventNames.*` so this module has no runtime dependency
// on @carvana/core-events; `satisfies` still type-checks each one.
export const MFE_MODAL_DEFINITIONS = {
  // The header MFE's signal that CoreAccountModal is safe to receive openAccountModal calls.
  accountModal: {
    moduleName: ACCOUNT_MODAL_MODULE,
    openEvent: 'ACCOUNT_MODAL_OPEN',
    readyEvent: 'ACCOUNT_MODAL_READY',
    replayReady: true
  },
  // Remounts per page — no replay, so a stale ready from a torn-down instance isn't revived.
  softpull: {
    datadogActionPrefix: 'softpull-mfe',
    moduleName: SOFTPULL_MODULE,
    openEvent: 'SOFTPULL_MODAL_OPEN',
    readyEvent: 'SOFTPULL_MODAL_READY',
    replayReady: false
  }
} as const satisfies Record<string, MfeModalDefinition>;

export const MFE_DEFINITIONS = {
  ...MFE_MODAL_DEFINITIONS,
  // The Header MFE persists across SRP/VDP SPA navigations, so CHAT_UI_READY only fires once
  // per page session; replay it to consumers that mount later.
  chatUi: {
    moduleName: CHAT_UI_MODULE,
    readyEvent: 'CHAT_UI_READY',
    replayReady: true
  },
  // ComposerContextProvider mounts once per session and never remounts on SPA nav, so
  // COMPOSER_READY only fires once; replay it to consumers that subscribe afterwards.
  composer: {
    moduleName: COMPOSER_MODULE,
    readyEvent: 'COMPOSER_READY',
    readySource: 'composer',
    replayReady: true,
    unreadyEvent: 'COMPOSER_UNREADY'
  }
} as const satisfies Record<string, MfeDefinition>;

export type MfeKey = keyof typeof MFE_DEFINITIONS;
export type MfeModalKey = keyof typeof MFE_MODAL_DEFINITIONS;

// Key constants, so call sites read `useMfeReady(mfes.composer)` rather than a bare string.
export const mfeModals = {
  accountModal: 'accountModal',
  softpull: 'softpull'
} as const satisfies Record<MfeModalKey, MfeModalKey>;

export const mfes = {
  ...mfeModals,
  chatUi: 'chatUi',
  composer: 'composer'
} as const satisfies Record<MfeKey, MfeKey>;

export type MfeModalPayload<Key extends MfeModalKey> =
  Events[(typeof MFE_MODAL_DEFINITIONS)[Key]['openEvent']];

export const ALL_MFE_DEFINITIONS: Array<MfeDefinition> =
  Object.values(MFE_DEFINITIONS);

export function getMfeDefinition(key: MfeKey): MfeDefinition {
  return MFE_DEFINITIONS[key];
}

export function isFromMfeSource(payload: unknown, source?: string) {
  if (!source) return true;

  return (
    typeof payload === 'object' &&
    payload !== null &&
    'source' in payload &&
    payload.source === source
  );
}
